import * as Sentry from "@sentry/react";

// Frontend errors go to Sentry, next to the backend's, so the nightly Sentry
// review triages them. Timing and web vitals stay in Axiom (observability.ts).

type SentryIntegration = Exclude<
  NonNullable<Parameters<typeof Sentry.init>[0]>["integrations"],
  undefined | ((...args: never[]) => unknown)
>[number];

interface ErrorReportingConfig {
  dsn: string;
  environment: string;
  // A string drops any error whose message contains it; use an anchored
  // RegExp to drop only an exact message.
  ignoreErrors?: Array<string | RegExp>;
  integrations?: SentryIntegration[];
}

export function initErrorReporting(config: ErrorReportingConfig): void {
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    // Errors only: tracing stays in Axiom, and PostHog records sessions.
    tracesSampleRate: 0,
    ignoreErrors: config.ignoreErrors ?? [],
    integrations: config.integrations ?? [],
  });
}

// Sentry drops tag values longer than this, so longer context (a component
// stack) goes in extra instead, where it is shown but not searchable.
const MAX_TAG_LENGTH = 200;

// Does nothing until initErrorReporting has run, so local dev and tests
// report nowhere.
export function captureException(
  error: unknown,
  context: Record<string, string> = {},
): void {
  const entries = Object.entries(context);
  Sentry.captureException(error, {
    tags: Object.fromEntries(
      entries.filter(([, value]) => value.length <= MAX_TAG_LENGTH),
    ),
    extra: Object.fromEntries(
      entries.filter(([, value]) => value.length > MAX_TAG_LENGTH),
    ),
  });
}

export function setUserContext(userId: string, email?: string): void {
  Sentry.setUser({ id: userId, ...(email && { email }) });
}
